import { deriveFilterTag, FilterTag } from "./deriveFilterTag";

/**
 * Derives a pseudo-random filter tag for a given public view key.
 * Useful for ephemeral or per-message tag generation without coordination.
 *
 * @param viewKey 32-byte public view key
 * @param bits Number of bits to use (default: 24)
 * @param entropy Optional entropy seed (8–32 bytes recommended).
 *        Advanced use only: if you provide this, make sure it is
 *        truly random or derived securely (e.g. from a strong mnemonic seed).
 *        Reusing or poorly deriving entropy can destroy unlinkability and leak identity.
 *
 * @returns A single FilterTag object (with bits of precision and padded 5-byte output)
 */
export function deriveRandomFilterTag({
  viewKey,
  bits,
  entropy,
}: {
  viewKey: Uint8Array;
  bits: number;
  entropy?: Uint8Array;
}): FilterTag {
  if (bits < 1 || bits > 40) {
    throw new Error("bits must be between 1 and 40");
  }
  if (viewKey.length !== 32) {
    throw new Error("viewKey must be 32 bytes");
  }

  const entropySource = entropy ?? crypto.getRandomValues(new Uint8Array(8));

  // Convert first 4 bytes of entropy to a little-endian u32 index
  const index =
    entropySource[0] |
    (entropySource[1] << 8) |
    (entropySource[2] << 16) |
    (entropySource[3] << 24);

  return deriveFilterTag({
    viewKey, 
    index: index >>> 0, 
    bits
  });
}